import { useCallback, useEffect } from 'react';
import {
  useReportDefinition,
  useReportFiltersManagement,
  useReportSectionSync,
  useReportExport,
} from '@reports/presentation/hooks';
import { ReportTypes } from '@reports/domain/types';
import {
  ReportBreadcrumb,
  ReportFiltersBar,
  ReportHeader,
  ReportSection,
  ReportTabs,
} from '@reports/presentation/components/base';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ReportSection as ReportSectionType } from '@reports/domain/value-objects';

/**
 * Cancellation Report Content
 *
 * Main content component for the cancellation report.
 * Displays breadcrumb, header, filters bar, tabs, and sections.
 * Export toasts are handled automatically by the useReportExport hook.
 * Infrastructure dependencies are managed by composition root.
 */
export const CancellationReportContent = () => {
  const { getReportDefinition } = useReportDefinition();
  const cancellationReport = getReportDefinition(ReportTypes.CANCELLATIONS);

  const { appliedFilters, updateFilters, selectReport } = useReportFiltersManagement({
    autoLoad: true,
    autoSave: true,
  });

  const { activeSectionKey, handleSectionChange } = useReportSectionSync({
    sections: cancellationReport.sections,
  });

  const { exportReport, isLoading } = useReportExport();

  useEffect(() => {
    selectReport(cancellationReport);
  }, []);

  const handleFiltersChange = useCallback(
    (filters: BlockDataRequestDto) => {
      updateFilters(filters);
    },
    [updateFilters],
  );

  const handleExport = useCallback(() => {
    exportReport(appliedFilters, cancellationReport.export);
  }, [appliedFilters, exportReport, cancellationReport.export]);

  return (
    <div className="w-full p-6 font-sans">
      <ReportBreadcrumb items={[{ label: cancellationReport.metadata.name }]} />

      <ReportHeader report={cancellationReport} />

      <ReportFiltersBar
        appliedFilters={appliedFilters}
        isExporting={isLoading}
        onExport={handleExport}
        onFiltersChange={handleFiltersChange}
        reportDefinition={cancellationReport}
      />

      <ReportTabs
        activeSectionKey={activeSectionKey}
        onSectionChange={handleSectionChange}
        sections={cancellationReport.sections}
      >
        {(section: ReportSectionType) => <ReportSection request={appliedFilters} section={section} />}
      </ReportTabs>
    </div>
  );
};
